export type UserEventPayload =
  | { type: 'click'; testId: string }
  | { type: 'type'; testId: string; value: string }
  | { type: 'press'; testId: string; key: 'enter' | 'esc' }
  | { type: 'select'; testId: string; value: string | number }
  | { type: 'check'; testId: string; checked: boolean };

export type UserEvent = UserEventPayload & {
  timestamp: number;
  route?: string;
};

let ENABLE_CY_CAPTURE = false;

export const initEventLogger = (enabled: boolean) => {
  ENABLE_CY_CAPTURE = enabled;

  if (enabled && typeof window !== 'undefined') {
    attachRecorderToWindow();
  }
};

const isCaptureEnabled = () => ENABLE_CY_CAPTURE;

const escapeForSingleQuotes = (s: string) => s.replace(/\\/g, '\\\\').replace(/'/g, "\\'");

const assertNever = (value: never): never => {
  throw new Error(`Unhandled event type: ${JSON.stringify(value)}`);
};

export const toCypressSnippet = (ev: UserEventPayload): string => {
  const get = `cy.get('[data-testid="${ev.testId}"]')`;

  switch (ev.type) {
    case 'click':
      return `${get}.click()`;

    case 'type': {
      const value = escapeForSingleQuotes(ev.value);
      return `${get}.clear().type('${value}').should('have.value', '${value}')`;
    }

    case 'press':
      return ev.key === 'enter' ? `${get}.type('{enter}')` : `${get}.type('{esc}')`;

    case 'select': {
      const value = escapeForSingleQuotes(String(ev.value));
      return `${get}.select('${value}').should('have.value', '${value}')`;
    }

    case 'check':
      return ev.checked ? `${get}.check()` : `${get}.uncheck()`;

    default:
      return assertNever(ev);
  }
};

const buffer: string[] = [];
const MAX_LINES = 5000;

const pushLine = (line: string) => {
  buffer.push(line);
  if (buffer.length > MAX_LINES) buffer.shift();
};

export const getRecordedLines = () => [...buffer];

export const clearRecordedLines = () => (buffer.length = 0);

export const copyRecordedLinesToClipboard = async () => {
  const text = buffer.join('\n');

  await navigator.clipboard.writeText(text);

  return text;
};

export const printRecordedLines = () => console.info('[recorded-cy]\n' + buffer.join('\n'));

export const logUserEvent = (payload: UserEventPayload) =>
  window.__cyRecorder && recordCypressLine(toCypressSnippet(payload));

export const logRecorderNote = (note: string) => {
  recordCypressLine(`// ${note}`);
};

export const resolveTrackedElement = (target: HTMLElement) => {
  let el: HTMLElement | null = target;

  while (el) {
    if (
      el.matches(
        'input, select, textarea, button, a, [role="button"], [role="checkbox"], [role="option"], div[aria-selected][class*="ant-select"]'
      )
    ) {
      break;
    }
    el = el.parentElement;
  }

  if (!el) return null;

  return el.closest<HTMLElement>('[data-testid]') ?? null;
};

declare global {
  interface Window {
    __cyRecorder?: {
      getLines: () => string[];
      clear: () => void;
      print: () => void;
      copy: () => Promise<void>;
    };
  }
}

const attachRecorderToWindow = () => {
  if (window.__cyRecorder) return;

  window.__cyRecorder = {
    getLines: getRecordedLines,
    clear: clearRecordedLines,
    print: printRecordedLines,
    copy: async () => {
      await copyRecordedLinesToClipboard();

      console.info('[recorded-cy] copied to clipboard');
    }
  };
};

export const recordCypressLine = (line: string) => {
  if (!isCaptureEnabled()) return;

  pushLine(line);
  console.info('[recorded-cy]', line);
};
